// Homepage.js
import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  FiTrendingUp,
  FiCloud,
  FiDatabase,
  FiBarChart2,
  FiCheckCircle,
  FiArrowRight,
  FiPlay,
  FiPause,
  FiMapPin,
  FiZoomIn,
  FiZoomOut,
  FiRotateCcw,
  FiTarget,
  FiEye,
  FiHeart,
} from "react-icons/fi";
import axios from "axios";
import Testimonials from "./Testimonials";

/* ------------------------------
   AnimatedCounter (unchanged)
   ------------------------------ */
const AnimatedCounter = ({ value, suffix = "", duration = 150 }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let start = 0;
    const end = parseInt(value) || 0;
    if (start === end) return;

    // Ensure non-zero divisor
    const totalMilSecDur = Math.max(duration, 200);
    const steps = Math.max(end, 1);
    const incrementTime = Math.max(10, Math.floor(totalMilSecDur / steps));

    let timer = setInterval(() => {
      start += 1;
      setCount(start);
      if (start >= end) clearInterval(timer);
    }, incrementTime);

    return () => clearInterval(timer);
  }, [value, duration]);

  return (
    <span>
      {count}
      {suffix}
    </span>
  );
};

/* ------------------------------
   ISO mapping & helpers
   ------------------------------ */
/*
  A mapping from ISO2 (alpha-2) to ISO3 (alpha-3) for African countries.
  This helps match database codes (which may be 2-letter) to SVG IDs (which often are 3-letter).
*/
const ISO2_TO_3 = {
  DZ: "DZA",
  AO: "AGO",
  BJ: "BEN",
  BW: "BWA",
  BF: "BFA",
  BI: "BDI",
  CM: "CMR",
  CV: "CPV",
  CF: "CAF",
  TD: "TCD",
  KM: "COM",
  CG: "COG",
  CD: "COD",
  CI: "CIV",
  DJ: "DJI",
  EG: "EGY",
  GQ: "GNQ",
  ER: "ERI",
  ET: "ETH",
  GA: "GAB",
  GM: "GMB",
  GH: "GHA",
  GN: "GIN",
  GW: "GNB",
  KE: "KEN",
  LS: "LSO",
  LR: "LBR",
  LY: "LBY",
  MG: "MDG",
  MW: "MWI",
  ML: "MLI",
  MR: "MRT",
  MU: "MUS",
  MA: "MAR",
  MZ: "MOZ",
  NA: "NAM",
  NE: "NER",
  NG: "NGA",
  RW: "RWA",
  ST: "STP",
  SN: "SEN",
  SC: "SYC",
  SL: "SLE",
  SO: "SOM",
  ZA: "ZAF",
  SS: "SSD",
  SD: "SDN",
  SZ: "SWZ",
  TZ: "TZA",
  TG: "TGO",
  TN: "TUN",
  UG: "UGA",
  EH: "ESH",
  ZM: "ZMB",
  ZW: "ZWE",
  BI: "BDI",
  LY: "LBY",
  ER: "ERI", // duplicates ok
  // Add more if needed
};

/* Normalize strings for reliable matching: remove non-alphanum and lowercase */
const normalize = (s = "") =>
  String(s)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // strip diacritics
    .replace(/[^a-z0-9]/g, "");

/* ------------------------------
   AfricaMap component (embedded)
   ------------------------------ */
const AfricaMap = ({
  countries,
  selectedCountry,
  onCountrySelect,
  highlightedCountry,
}) => {
  const [svgContent, setSvgContent] = useState("");
  const mapRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [posX, setPosX] = useState(0);
  const [posY, setPosY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startX = useRef(0);
  const startY = useRef(0);

  // Load the SVG
  useEffect(() => {
    // You can swap this URL for a different SVG as needed
    const url =
      "https://upload.wikimedia.org/wikipedia/commons/f/f9/BlankMap-Africa.svg";

    fetch(url)
      .then((res) => res.text())
      .then((svgText) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(svgText, "image/svg+xml");
        const svg = doc.querySelector("svg");
        if (svg) {
          // Make sure viewBox and sizing are present so scaling works predictably
          if (!svg.getAttribute("viewBox")) {
            // Many Wikimedia maps already have a viewBox; if not, set a default
            svg.setAttribute("viewBox", "0 0 1000 1000");
          }
          svg.setAttribute("width", "100%");
          svg.setAttribute("height", "100%");
          svg.setAttribute("preserveAspectRatio", "xMidYMid meet");

          // Wrap children in a group to apply transforms (pan/zoom)
          const g = doc.createElementNS("http://www.w3.org/2000/svg", "g");
          g.id = "map-group";
          while (svg.firstChild) {
            g.appendChild(svg.firstChild);
          }
          svg.appendChild(g);
        }

        const serializer = new XMLSerializer();
        setSvgContent(serializer.serializeToString(doc));
      })
      .catch((err) => {
        console.error("Error loading SVG:", err);
        // fallback: tiny placeholder
        setSvgContent(
          `<svg viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg"><g id="map-group"><rect x="0" y="0" width="1000" height="1000" fill="#eef2f7"/><text x="500" y="500" text-anchor="middle" dominant-baseline="middle" fill="#111">Africa Map</text></g></svg>`
        );
      });
  }, []);

  // apply pan/zoom transform to group
  useEffect(() => {
    const group = mapRef.current?.querySelector("#map-group");
    if (group) {
      group.setAttribute(
        "transform",
        `translate(${posX}, ${posY}) scale(${zoom})`
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom, posX, posY]);

  // function that attempts to match an SVG path element to a country object
  const findMatchingCountryForPath = (path, countriesList) => {
    if (!path) return null;
    const id = path.id || "";
    const title =
      path.getAttribute("title") || path.getAttribute("data-name") || "";
    const labelCandidates = [id, title].map((s) => normalize(s));

    for (const c of countriesList) {
      const code = (c.code || "").toUpperCase().trim(); // code from backend (could be 'NG' or 'NGA')
      const name = normalize(c.name || "");
      const code2 = normalize(code); // normalized code
      const code3 = ISO2_TO_3[code] ? normalize(ISO2_TO_3[code]) : ""; // mapped 3-letter
      const altMatches = [code2, code3, name].filter(Boolean);

      // If any path label candidate matches any altMatches, we have a match
      for (const label of labelCandidates) {
        if (!label) continue;
        for (const alt of altMatches) {
          if (!alt) continue;
          if (label === alt) return c;
        }
      }
    }

    // Last resort: match by fuzzy name compare (startsWith)
    const nid = normalize(id);
    if (nid) {
      const fuzzy = countriesList.find((c) =>
        normalize(c.name || "").startsWith(nid)
      );
      if (fuzzy) return fuzzy;
    }

    return null;
  };

   

  // set click handlers & base styles
  useEffect(() => {
    if (!svgContent || !mapRef.current) return;

    const paths = mapRef.current.querySelectorAll("path, polygon, rect, g");
    paths.forEach((path) => {
      // Try to ignore decorative shapes that don't represent countries
      // (we only care about those with IDs / titles)
      const id = (path.id || "").trim();
      const title = (path.getAttribute("title") || "").trim();
      if (!id && !title) {
        // still assign base style, but no click
        path.style.cursor = "default";
        path.style.transition = "all 0.25s ease";
        path.style.stroke = "#059669";
        path.style.strokeWidth = "0.4";
        return;
      }

      // Clear previous handlers
      path.onclick = null;

      // Find matching country using robust logic
      const matched = findMatchingCountryForPath(path, countries);
      if (matched) {
        path.style.cursor = "pointer";
        path.onclick = (e) => {
          e.stopPropagation();
          try {
            onCountrySelect(matched);
            // optional console debug
            console.log(
              "Map click -> matched country:",
              matched.name,
              matched.code,
              "path id:",
              path.id || title
            );
          } catch (err) {
            console.error("Error on path click:", err);
          }
        };
      } else {
        path.style.cursor = "default";
      }

      // base visual
      path.style.transition = "all 0.3s ease";
      path.style.stroke = "#059669";
      path.style.strokeWidth = "0.5";
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [svgContent, countries, onCountrySelect]);

  // highlighting logic
  useEffect(() => {
    if (!svgContent || !mapRef.current) return;
    const paths = mapRef.current.querySelectorAll("path, polygon, rect, g");

    const highlightedNorm = highlightedCountry
      ? normalize(highlightedCountry)
      : null;
    const highlightedIso3 =
      highlightedCountry && ISO2_TO_3[highlightedCountry.toUpperCase()]
        ? normalize(ISO2_TO_3[highlightedCountry.toUpperCase()])
        : null;

    paths.forEach((path) => {
      const id = path.id || "";
      const title = path.getAttribute("title") || "";
      const label = normalize(id || title);

      const isActive = countries.some((c) => {
        const code = (c.code || "").toUpperCase().trim();
        const name = normalize(c.name || "");
        const code2 = normalize(code);
        const code3 = ISO2_TO_3[code] ? normalize(ISO2_TO_3[code]) : "";
        return label === code2 || label === code3 || label === name;
      });

      let isHighlighted = false;
      if (
        highlightedNorm &&
        (label === highlightedNorm || label === highlightedIso3)
      )
        isHighlighted = true;
      // Also test if highlightedCountry is a country object code looked up in countries list
      if (!isHighlighted && highlightedCountry) {
        const matched = countries.find((c) => {
          const code = (c.code || "").toUpperCase().trim();
          return (
            code === highlightedCountry.toUpperCase() ||
            ISO2_TO_3[code] === highlightedCountry.toUpperCase()
          );
        });
        if (matched) {
          const matchName = normalize(matched.name || "");
          if (label === matchName) isHighlighted = true;
        }
      }

      if (isHighlighted) {
        path.style.fill = "#ef4444";
        path.style.stroke = "#dc2626";
        path.style.strokeWidth = "1.8";
        path.style.filter = "brightness(1.08)";
        path.style.fillOpacity = "1";
      } else if (isActive) {
        path.style.fill = "#10B981";
        path.style.fillOpacity = "0.65";
        path.style.strokeWidth = "0.5";
        path.style.filter = "";
      } else {
        path.style.fill = "#d1d5db";
        path.style.fillOpacity = "0.35";
        path.style.filter = "";
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [svgContent, highlightedCountry, countries]);

  // wheel zoom
  useEffect(() => {
    const el = mapRef.current;
    if (!el) return;
    const handleWheel = (e) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? 0.92 : 1.08;
      setZoom((z) => Math.max(0.5, Math.min(3, z * delta)));
    };
    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, []);

  // pan handlers
  useEffect(() => {
    const el = mapRef.current;
    if (!el) return;

    const onDown = (e) => {
      if (e.button !== 0) return;
      setIsDragging(true);
      startX.current = e.clientX - posX;
      startY.current = e.clientY - posY;
    };
    const onMove = (e) => {
      if (!isDragging) return;
      setPosX(e.clientX - startX.current);
      setPosY(e.clientY - startY.current);
    };
    const onUp = () => setIsDragging(false);

    el.addEventListener("mousedown", onDown);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    el.addEventListener("mouseleave", onUp);

    return () => {
      el.removeEventListener("mousedown", onDown);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      el.removeEventListener("mouseleave", onUp);
    };
  }, [isDragging, posX, posY]);

  const zoomIn = () => setZoom((z) => Math.min(3, z * 1.2));
  const zoomOut = () => setZoom((z) => Math.max(0.5, z / 1.2));
  const resetView = () => {
    setZoom(1);
    setPosX(0);
    setPosY(0);
  };

  return (
    <div className="relative w-full h-[600px] bg-blue-50 rounded-lg overflow-hidden border-2 border-green-200 select-none">
      <div
        ref={mapRef}
        className="w-full h-full cursor-grab active:cursor-grabbing"
        dangerouslySetInnerHTML={{ __html: svgContent }}
      />

      {/* Zoom Controls */}
      <div className="absolute top-4 right-4 flex flex-col gap-2 bg-white/95 backdrop-blur-sm rounded-xl p-2 shadow-2xl border border-green-200 z-30">
        <button
          onClick={zoomIn}
          title="Zoom In"
          className="w-10 h-10 bg-green-500 hover:bg-green-600 text-white rounded-lg flex items-center justify-center"
        >
          <FiZoomIn className="w-5 h-5" />
        </button>
        <button
          onClick={zoomOut}
          title="Zoom Out"
          className="w-10 h-10 bg-green-500 hover:bg-green-600 text-white rounded-lg flex items-center justify-center"
        >
          <FiZoomOut className="w-5 h-5" />
        </button>
        <button
          onClick={resetView}
          title="Reset View"
          className="w-10 h-10 bg-gray-500 hover:bg-gray-600 text-white rounded-lg flex items-center justify-center"
        >
          <FiRotateCcw className="w-5 h-5" />
        </button>
      </div>

      {/* Overlay */}
      <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-2xl border border-green-200 z-20">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center">
            <FiMapPin className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-gray-800 text-lg">
              African Operations
            </h3>
            <p className="text-green-600 text-sm font-semibold">
              {countries.length} Countries Active
            </p>
          </div>
        </div>
        <p className="text-gray-600 text-xs max-w-[200px]">
          Click an active country to view projects. Scroll to zoom, drag to pan.
        </p>
      </div>
    </div>
  );
};

/* ------------------------------
   Homepage (rest of your page)
   Mostly unchanged — only small wiring to the inline AfricaMap above
   ------------------------------ */

function Homepage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [heroSlides, setHeroSlides] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoPlay, setAutoPlay] = useState(true);

  // Stats and countries state
  const [stats, setStats] = useState({
    farmsTransformed: 0,
    yieldIncrease: 0,
    africanCountries: 0,
    clientSatisfaction: 0,
  });

  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [highlightedCountry, setHighlightedCountry] = useState(null);
  const [summary, setSummary] = useState({
    totalCountries: 0,
    totalProjects: 0,
    totalFarms: 0,
    yearsExperience: 10,
  });

  const defaultCarouselImages = [
    {
      image:
        "https://media.istockphoto.com/id/2150078427/photo/young-farmer-woman-is-inspecting-and-controlling-a-drone-in-a-green-wheat-field-modern.jpg?s=2048x2048&w=is&k=20&c=xgZVgy_pNY8YLMtXocyo8B9yE5BWTJje4zIQZjvx_Kc=",
      title: "Smart Farming Revolution",
      subtitle: "AI-Powered Agricultural Solutions",
      description:
        "Transforming traditional farming with cutting-edge technology and data-driven insights",
      cta: "Explore Solutions",
    },
    {
      image:
        "https://images.unsplash.com/photo-1586771107445-d3ca888129ff?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      title: "Precision Agriculture",
      subtitle: "Optimize Every Square Meter",
      description:
        "Maximize yield and reduce waste with sensor technology and real-time monitoring",
      cta: "Learn More",
    },
    {
      image:
        "https://images.unsplash.com/photo-1625246333195-78d9c38ad449?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
      title: "Welcome to Afrikonsults",
      subtitle: "Agric Development Ltd",
      description:
        "Transforming African agriculture through innovative technology solutions and sustainable development practices",
      cta: "Discover Our Vision",
      link: "/about",
    },
    {
      image:
        "https://images.unsplash.com/photo-1574943320219-553eb213f72d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
      title: "Smart Farming Solutions",
      subtitle: "Precision Agriculture Experts",
      description:
        "Leveraging IoT, AI, and data analytics to optimize crop yields and resource management across Africa",
      cta: "Explore Solutions",
      link: "/services",
    },
    {
      image:
        "https://images.unsplash.com/photo-1592982537447-7448a4615bdc?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
      title: "Pan-African Impact",
      subtitle: "15+ Countries Served",
      description:
        "Building sustainable agricultural ecosystems across the continent with locally-adapted technology solutions",
      cta: "View Our Reach",
      link: "/projects",
    },
    {
      image:
        "https://images.unsplash.com/photo-1625246333195-78d9c38ad449?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
      title: "Innovation in Agriculture",
      subtitle: "Research & Development",
      description:
        "Pioneering agricultural technologies tailored for African climates, crops, and farming communities",
      cta: "Learn About R&D",
      link: "/technology",
    },
    {
      image:
        "https://images.unsplash.com/photo-1586771107445-d3ca888129ff?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
      title: "Partner With Us",
      subtitle: "Transform Your Agricultural Projects",
      description:
        "Join leading organizations and governments in revolutionizing agriculture across Africa",
      cta: "Start Partnership",
      link: "/contact",
    },
  ];


  const values = [
      {
        icon: <FiTarget className="w-8 h-8" />,
        title: "Our Mission",
        description:
          "To enhance food and nutrition security and income of farmers by improving delivery of agricultural technologies, products, and services.",
        gradient: "from-green-500 to-emerald-500",
      },
      {
        icon: <FiEye className="w-8 h-8" />,
        title: "Our Vision",
        description:
          "To contribute to agricultural development through use of innovative technologies, products, and services.",
        gradient: "from-blue-500 to-cyan-500",
      },
      {
        icon: <FiHeart className="w-8 h-8" />,
        title: "Our Values",
        description:
          "Innovation, sustainability, and empowerment drive everything we do for the agricultural community.",
        gradient: "from-orange-500 to-red-500",
      },
    ];

  useEffect(() => {
    fetchHomepageData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-advance carousel
  useEffect(() => {
    if (!autoPlay) return;
    const slides = getCurrentSlides();
    if (!slides.length) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heroSlides, autoPlay]);

  const fetchHomepageData = async () => {
    try {
      setLoading(true);
      console.log("Starting to fetch homepage data...");

      // Fetch stats
      try {
        const statsResponse = await axios.get(
          "https://agri-back-mauve.vercel.app/api/stats/public"
        );
        console.log("Stats response:", statsResponse.data);
        if (statsResponse.data.success) {
          setStats(statsResponse.data.data);
        }
      } catch (statsError) {
        console.error("Error fetching stats:", statsError);
        setStats({
          farmsTransformed: 250,
          yieldIncrease: 40,
          africanCountries: 15,
          clientSatisfaction: 98,
        });
      }

      // Fetch countries
      try {
        const countriesResponse = await axios.get(
          "https://agri-back-mauve.vercel.app/api/countries/public"
        );
        console.log("Countries response:", countriesResponse.data);
        if (countriesResponse.data.success) {
          setCountries(countriesResponse.data.data || []);
          setSummary(
            countriesResponse.data.summary || {
              totalCountries: 0,
              totalProjects: 0,
              totalFarms: 0,
              yearsExperience: 10,
            }
          );
          console.log("Countries loaded:", countriesResponse.data.data?.length);
        }
      } catch (countriesError) {
        console.error("Error fetching countries:", countriesError);
        setCountries([]);
        setSummary({
          totalCountries: 0,
          totalProjects: 0,
          totalFarms: 0,
          yearsExperience: 10,
        });
      }

      // Fetch hero slides
      // try {
      //   const heroResponse = await axios.get(
      //     "https://agri-back-mauve.vercel.app/api/content/public/hero"
      //   );
      //   if (heroResponse.data.success && heroResponse.data.data.length > 0) {
      //     setHeroSlides(heroResponse.data.data);
      //   }
      // } catch (heroError) {
      //   console.error("Error fetching hero slides:", heroError);
      //   setHeroSlides([]);
      // }

      // Fetch services
      try {
        const servicesResponse = await axios.get(
          "https://agri-back-mauve.vercel.app/api/content/public/service?limit=4"
        );
        if (servicesResponse.data.success) {
          setServices(servicesResponse.data.data);
        }
      } catch (servicesError) {
        console.error("Error fetching services:", servicesError);
        setServices([]);
      }
    } catch (error) {
      console.error("Error in fetchHomepageData:", error);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentSlides = () => {
    const validAdminSlides = heroSlides.filter(
      (slide) => slide.image && slide.image.trim() !== ""
    );
    return [...validAdminSlides, ...defaultCarouselImages];
  };

  const handleCountrySelect = (country) => {
    console.log("Country selected (UI):", country);
    setSelectedCountry(country);
    // set highlighted country to either its code (prefer backend code) or name if no code
    setHighlightedCountry(country.code || country.name);
  };

  const handleCloseCountryDetails = () => {
    setSelectedCountry(null);
    setHighlightedCountry(null);
  };

  const nextSlide = () => {
    const slides = getCurrentSlides();
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    const slides = getCurrentSlides();
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const getIconComponent = (iconName) => {
    const icons = {
      precision: FiTrendingUp,
      iot: FiCloud,
      data: FiDatabase,
      drone: FiBarChart2,
      monitoring: FiBarChart2,
      analysis: FiDatabase,
      consulting: FiTrendingUp,
    };
    const IconComponent = icons[iconName] || FiTrendingUp;
    return <IconComponent className="w-8 h-8" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
          <p className="text-green-700 font-semibold">
            LOADING AFRIKONSULTS AGRIC DEVELOPMENT LTD...
          </p>
        </div>
      </div>
    );
  }

  const slides = getCurrentSlides();

  return (
    <div className="homepage-container bg-gradient-to-b from-gray-50 to-green-50">
      {/* Hero Section */}
      <section className="hero-section relative h-screen overflow-hidden">
        <div className="carousel-container relative h-full">
          <AnimatePresence mode="wait">
            {slides.length > 0 && (
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0, scale: 1.1 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1 }}
                transition={{ duration: 1.2, ease: "easeOut" }}
                className="carousel-slide absolute inset-0"
              >
                <div
                  className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                  style={{
                    backgroundImage: `url(${slides[currentSlide].image})`,
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>

                <div className="relative h-full flex items-end pb-20 lg:pb-32">
                  <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-4xl">
                      <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.3 }}
                        className="bg-white/10 backdrop-blur-md rounded-3xl p-8 lg:p-12 border border-white/20 shadow-2xl"
                      >
                        <motion.h1
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.8, delay: 0.4 }}
                          className="hero-title text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-4 leading-tight"
                        >
                          {slides[currentSlide].title ||
                            "Transforming African Agriculture"}
                        </motion.h1>

                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.8, delay: 0.5 }}
                          className="mb-6"
                        >
                          <span className="inline-block px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-lg font-semibold rounded-full mb-4">
                            {slides[currentSlide].subtitle ||
                              "Innovative Technology Solutions"}
                          </span>
                        </motion.div>

                        <motion.p
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.8, delay: 0.6 }}
                          className="text-xl lg:text-2xl text-white/90 mb-8 leading-relaxed font-light"
                        >
                          {slides[currentSlide].description ||
                            "Boost productivity across the African continent with our innovative solutions"}
                        </motion.p>

                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.8, delay: 0.7 }}
                          className="hero-buttons flex flex-col sm:flex-row gap-4"
                        >
                          <Link
                            to="/contact"
                            className="group bg-gradient-to-r from-green-500 to-emerald-500 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:from-green-600 hover:to-emerald-600 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 flex items-center justify-center gap-3"
                          >
                            {slides[currentSlide].cta || "Get Started Today"}
                            <FiArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                          </Link>
                          <Link
                            to="/services"
                            className="group border-2 border-white text-white px-8 py-4 rounded-2xl font-bold text-lg hover:bg-white hover:text-green-700 transition-all duration-300 backdrop-blur-sm hover:scale-105 flex items-center justify-center gap-3"
                          >
                            View Solutions
                            <FiArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                          </Link>
                        </motion.div>
                      </motion.div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {slides.length > 0 && (
            <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex items-center gap-6">
              <button
                onClick={() => setAutoPlay(!autoPlay)}
                className="bg-white/20 backdrop-blur-md text-white p-3 rounded-full hover:bg-white/30 transition-all duration-300 border border-white/30"
              >
                {autoPlay ? (
                  <FiPause className="w-5 h-5" />
                ) : (
                  <FiPlay className="w-5 h-5" />
                )}
              </button>

              <div className="flex gap-2">
                <button
                  className="carousel-control bg-white/20 backdrop-blur-md text-white p-3 rounded-full hover:bg-white/30 transition-all duration-300 border border-white/30"
                  onClick={prevSlide}
                >
                  ‹
                </button>
                <button
                  className="carousel-control bg-white/20 backdrop-blur-md text-white p-3 rounded-full hover:bg-white/30 transition-all duration-300 border border-white/30"
                  onClick={nextSlide}
                >
                  ›
                </button>
              </div>

              <div className="flex gap-3">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    className={`indicator transition-all duration-500 ${
                      index === currentSlide
                        ? "w-8 bg-white rounded-full"
                        : "w-3 bg-white/50 rounded-full hover:bg-white/70"
                    } h-3`}
                    onClick={() => setCurrentSlide(index)}
                  />
                ))}
              </div>
            </div>
          )}

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
            className="absolute bottom-4 left-1/2 transform -translate-x-1/2 hidden lg:block"
          >
            <div className="flex flex-col items-center text-white/70">
              <span className="text-sm mb-2">Scroll Down</span>
              <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
                <motion.div
                  animate={{ y: [0, 12, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  className="w-1 h-3 bg-white/50 rounded-full mt-2"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Company Introduction Section */}
      <section className="company-intro-section py-20 bg-gradient-to-br from-white to-emerald-50 dark:from-gray-900 dark:to-emerald-900/20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Content Column */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="space-y-8"
              >
                {/* Company Badge */}
                <div className="inline-flex items-center gap-3 px-6 py-3 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-sm font-semibold border border-green-200 dark:border-green-700">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  OFFICIALLY REGISTERED & CERTIFIED
                </div>

                {/* Company Name & Registration */}
                <div className="space-y-4">
                  <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white leading-tight">
                    <span className="bg-gradient-to-r from-green-600 to-emerald-600 dark:from-green-400 dark:to-emerald-400 bg-clip-text text-transparent">
                     WELCOME TO AFRIKONSULTS
                    </span>
                    <br />
                    <span className="text-2xl md:text-3xl text-gray-600 dark:text-gray-300 font-light">
                      Agric Development Ltd
                    </span>
                  </h2>

                  {/* Registration Badge */}
                  <div className="inline-flex items-center gap-3 bg-white dark:bg-gray-800 rounded-2xl px-6 py-4 shadow-lg border border-green-200 dark:border-green-700">
                    <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-sm">✓</span>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        CAC Registration
                      </p>
                      <p className="text-green-600 dark:text-green-400 font-semibold">
                        Ref: 17344282201 • Dec 2024
                      </p>
                    </div>
                  </div>
                </div>

                {/* Company Description */}
                <div className="space-y-6">
                  <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
                    <span className="font-semibold text-green-600 dark:text-green-400">
                      Afrikonsults Agric Development Ltd (AFRIKONSULTS)
                    </span>{" "}
                    is a Private Limited Liability Company, incorporated in
                    Nigeria by the Corporate Affairs Commission (CAC) in 2024.
                  </p>

                  <div className="bg-green-50 dark:bg-green-900/20 rounded-2xl p-6 border border-green-200 dark:border-green-700">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                      Our Mission
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                      To provide technical agricultural services, strategic
                      planning, leadership, and capacity enhancement to
                      individuals and local and international organizations
                      involved in agricultural development to improve food and
                      nutrition security, and income of farmers in Africa.
                    </p>
                  </div>
                </div>

                {/* Key Focus Areas */}
                <div className="grid grid-cols-2 gap-4">
                  {[
                    {
                      icon: "🌱",
                      label: "Food Security",
                      color: "text-green-600",
                    },
                    {
                      icon: "📈",
                      label: "Income Growth",
                      color: "text-emerald-600",
                    },
                    {
                      icon: "🔬",
                      label: "Technical Services",
                      color: "text-green-600",
                    },
                    {
                      icon: "🌍",
                      label: "African Focus",
                      color: "text-emerald-600",
                    },
                  ].map((item, index) => (
                    <motion.div
                      key={item.label}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 + index * 0.1 }}
                      className="flex items-center gap-3 bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-green-100 dark:border-green-800"
                    >
                      <span className="text-2xl">{item.icon}</span>
                      <span
                        className={`font-semibold ${item.color} dark:text-green-400`}
                      >
                        {item.label}
                      </span>
                    </motion.div>
                  ))}
                </div>

                {/* CTA Buttons */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="flex flex-col sm:flex-row gap-4 pt-6"
                >
                  <Link
                    to="/about"
                    className="group bg-gradient-to-r from-green-500 to-emerald-500 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:from-green-600 hover:to-emerald-600 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 flex items-center justify-center gap-3"
                  >
                    Learn More About Us
                    <FiArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <Link
                    to="/contact"
                    className="group border-2 border-green-500 text-green-600 dark:text-green-400 px-8 py-4 rounded-2xl font-bold text-lg hover:bg-green-500 hover:text-white transition-all duration-300 hover:scale-105 flex items-center justify-center gap-3"
                  >
                    Partner With Us
                    <FiArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </motion.div>
              </motion.div>

              {/* Visual Column */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="relative"
              >
                {/* Main Image */}
                <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src="https://images.unsplash.com/photo-1586771107445-d3ca888129ff?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
                    alt="Afrikonsults Agricultural Innovation"
                    className="w-full h-96 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>

                  {/* Overlay Content */}
                  <div className="absolute bottom-6 left-6 right-6 text-white">
                    <h3 className="text-2xl font-bold mb-2">
                      Transforming African Agriculture
                    </h3>
                    <p className="text-green-200">
                      Innovative solutions for sustainable development
                    </p>
                  </div>
                </div>

                {/* Floating Feature Cards */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="absolute -top-6 -right-6 bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-2xl border border-green-200 dark:border-green-700 z-10"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                      <span className="text-white text-xl">🚀</span>
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 dark:text-white">
                        Innovation
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Driven Approach
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Cutting-edge agricultural technology solutions
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                  className="absolute -bottom-6 -left-6 bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-2xl border border-green-200 dark:border-green-700 z-10"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-green-500 rounded-xl flex items-center justify-center">
                      <span className="text-white text-xl">🌍</span>
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 dark:text-white">
                        Pan-African
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Impact Focus
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Serving farmers across the African continent
                  </p>
                </motion.div>
              </motion.div>
            </div>

            {/* Trust Indicators */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="mt-16 pt-12 border-t border-green-200 dark:border-green-800"
            >
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                {[
                  {
                    icon: "🏛️",
                    label: "CAC Registered",
                    description: "Official Registration",
                  },
                  {
                    icon: "✓",
                    label: "Legal Entity",
                    description: "Limited Liability",
                  },
                  {
                    icon: "🎯",
                    label: "Specialized",
                    description: "Agricultural Focus",
                  },
                  {
                    icon: "🤝",
                    label: "Partnerships",
                    description: "International Reach",
                  },
                ].map((item, index) => (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    className="group"
                  >
                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 group-hover:scale-105 border border-green-100 dark:border-green-700">
                      <div className="text-3xl mb-3 group-hover:scale-110 transition-transform duration-300">
                        {item.icon}
                      </div>
                      <h4 className="font-bold text-gray-900 dark:text-white mb-1">
                        {item.label}
                      </h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {item.description}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>


       {/* Values Section */}
            <section className="py-20 bg-white">
              <div className="container mx-auto px-4">
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  className="text-center mb-16"
                >
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                    Our Core Values
                  </h2>
                  <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                    The principles that guide everything we do at Afrikonsults
                  </p>
                </motion.div>
      
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {values.map((value, index) => (
                    <motion.div
                      key={value.title}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.2 }}
                      whileHover={{ y: -10 }}
                      className="bg-white rounded-2xl shadow-lg p-8 text-center border border-gray-100 hover:shadow-xl transition-all duration-300"
                    >
                      <div
                        className={`w-20 h-20 mx-auto mb-6 bg-gradient-to-r ${value.gradient} rounded-2xl flex items-center justify-center text-white`}
                      >
                        {value.icon}
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">
                        {value.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {value.description}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </section>

      {/* Stats Section */}
      <section className="stats-section py-20 bg-gradient-to-br from-white to-green-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                number: stats.farmsTransformed,
                label: "Farms Transformed",
                suffix: "+",
                duration: 200,
              },
              {
                number: stats.yieldIncrease,
                label: "Yield Increase",
                suffix: "%",
                duration: 150,
              },
              {
                number: stats.africanCountries,
                label: "African Countries",
                suffix: "",
                duration: 100,
              },
              {
                number: stats.clientSatisfaction,
                label: "Client Satisfaction",
                suffix: "%",
                duration: 150,
              },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center group"
              >
                <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-500 group-hover:scale-105 border border-green-100">
                  <h3 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-3">
                    <AnimatedCounter
                      value={stat.number}
                      suffix={stat.suffix}
                      duration={stat.duration}
                    />
                  </h3>
                  <p className="text-gray-600 font-semibold text-lg">
                    {stat.label}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* African Presence Section */}
      <section className="presence-section py-20 bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="section-header text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-semibold mb-6">
              <FiMapPin className="w-4 h-4" />
              AFRICAN PRESENCE
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Serving{" "}
              <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                Across Africa
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              With operations in {summary.totalCountries} African countries,
              we're bringing innovative agricultural technology solutions to
              farmers across the continent.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Map Container */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              className="relative bg-white rounded-3xl shadow-2xl overflow-hidden border border-green-200"
            >
              <AfricaMap
                countries={countries}
                selectedCountry={selectedCountry}
                onCountrySelect={handleCountrySelect}
                highlightedCountry={highlightedCountry}
              />
            </motion.div>

            {/* Countries List and Details */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Our African Footprint
              </h3>

              {/* Countries Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
                {countries.length > 0 ? (
                  countries.map((country) => (
                    <motion.div
                      key={country._id}
                      whileHover={{ scale: 1.02 }}
                      className={`bg-white rounded-2xl p-4 shadow-lg border-2 transition-all duration-300 cursor-pointer ${
                        selectedCountry?._id === country._id
                          ? "border-green-500 bg-green-50"
                          : "border-green-100 hover:border-green-300"
                      }`}
                      onClick={() => handleCountrySelect(country)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                          <span className="text-xl">{country.flag}</span>
                          {country.name}
                        </h4>
                        <span className="text-sm text-green-600 font-semibold bg-green-100 px-2 py-1 rounded-full">
                          {country.capital}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <div className="text-center">
                          <div className="text-xl font-bold text-green-600">
                            {country.projects?.length || 0}
                          </div>
                          <div className="text-xs text-gray-500">Projects</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xl font-bold text-green-600">
                            {country.totalFarms || 0}
                          </div>
                          <div className="text-xs text-gray-500">Farms</div>
                        </div>
                      </div>

                      <p className="text-sm text-gray-600 leading-relaxed line-clamp-2">
                        {country.description}
                      </p>
                    </motion.div>
                  ))
                ) : (
                  <div className="col-span-2 text-center py-8 text-gray-500">
                    <FiMapPin className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No countries data available</p>
                    <p className="text-sm">
                      Check if countries are added in admin panel
                    </p>
                  </div>
                )}
              </div>

              {/* Selected Country Details */}
              {selectedCountry && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl p-6 text-white"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-xl font-bold flex items-center gap-3">
                      <span className="text-2xl">{selectedCountry.flag}</span>
                      {selectedCountry.name} Operations
                    </h4>
                    <button
                      onClick={handleCloseCountryDetails}
                      className="text-white/80 hover:text-white transition-colors text-xl"
                    >
                      ✕
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="text-center">
                      <div className="text-3xl font-bold">
                        {selectedCountry.projects?.length || 0}
                      </div>
                      <div className="text-sm opacity-90">Active Projects</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold">
                        {selectedCountry.totalFarms || 0}
                      </div>
                      <div className="text-sm opacity-90">Partner Farms</div>
                    </div>
                  </div>

                  <p className="text-green-100 leading-relaxed mb-4">
                    {selectedCountry.description}
                  </p>

                  {selectedCountry.projects &&
                    selectedCountry.projects.length > 0 && (
                      <div className="mt-4">
                        <h5 className="font-semibold mb-2">
                          Current Projects:
                        </h5>
                        <div className="space-y-2 max-h-32 overflow-y-auto">
                          {selectedCountry.projects.map((project, index) => (
                            <div
                              key={index}
                              className="bg-white/20 rounded-lg p-2"
                            >
                              <p className="font-medium text-sm">
                                {project.title}
                              </p>
                              <p className="text-xs opacity-90">
                                {project.status} • {project.progress}% complete
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                </motion.div>
              )}
            </div>
          </div>

          {/* Summary Stats */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 text-center"
          >
            {[
              {
                number: summary.totalCountries,
                label: "African Countries",
                icon: "🌍",
              },
              {
                number: summary.totalProjects,
                label: "Total Projects",
                icon: "🚀",
              },
              {
                number: summary.totalFarms,
                label: "Partner Farms",
                icon: "🌾",
              },
              {
                number: summary.yearsExperience,
                label: "Years Experience",
                icon: "⭐",
              },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-lg border border-green-100 hover:shadow-xl transition-all duration-300"
              >
                <div className="text-3xl mb-2">{stat.icon}</div>
                <div className="text-3xl font-bold text-green-600 mb-1">
                  <AnimatedCounter value={stat.number} />
                </div>
                <div className="text-gray-600 font-semibold">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Services Section - UPDATED WITH DARK MODE & PROFESSIONAL STYLING */}
      <section className="services-section py-20 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="section-header text-center mb-20"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-sm font-semibold mb-8 border border-green-200 dark:border-green-700"
            >
              <FiTrendingUp className="w-4 h-4" />
              INNOVATIVE SOLUTIONS
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-6"
            >
              OUR{" "}
              <span className="bg-gradient-to-r from-green-600 to-emerald-600 dark:from-green-400 dark:to-emerald-400 bg-clip-text text-transparent">
                AFRIKONSULTS
              </span>{" "}
              SOLUTIONS
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed"
            >
              Comprehensive technology solutions designed specifically for
              African agricultural challenges, empowering farmers with
              cutting-edge innovations and sustainable practices.
            </motion.p>
          </motion.div>

          {/* Services Grid */}
          <div className="services-grid grid grid-cols-1 lg:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service._id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -12, scale: 1.02 }}
                className="service-card group relative bg-white dark:bg-gray-800 rounded-3xl shadow-2xl hover:shadow-3xl dark:shadow-gray-900/50 p-8 transition-all duration-500 border border-green-100 dark:border-green-800 overflow-hidden"
              >
                {/* Background Gradient Effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/10 dark:to-emerald-900/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                {/* Animated Border */}
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-green-500 to-emerald-500 opacity-0 group-hover:opacity-5 transition-opacity duration-500"></div>

                <div className="relative z-10 flex items-start gap-6">
                  {/* Service Icon */}
                  <div className="service-icon-wrapper flex-shrink-0">
                    <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center text-white group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-lg">
                      {getIconComponent(service.icon)}
                    </div>
                  </div>

                  {/* Service Content */}
                  <div className="flex-1">
                    {/* Category Badge */}
                    {service.category && (
                      <span className="inline-block px-3 py-1 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 text-xs font-semibold rounded-full mb-4 border border-green-200 dark:border-green-700">
                        {service.category}
                      </span>
                    )}

                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 group-hover:text-green-700 dark:group-hover:text-green-400 transition-colors duration-300">
                      {service.title}
                    </h3>

                    <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed text-lg">
                      {service.description}
                    </p>

                    {/* Features List */}
                    {service.features && service.features.length > 0 && (
                      <ul className="service-features space-y-3 mb-8">
                        {service.features.slice(0, 3).map((feature, i) => (
                          <motion.li
                            key={i}
                            initial={{ opacity: 0, x: -10 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.3 + i * 0.1 }}
                            className="flex items-center text-gray-700 dark:text-gray-300 group/feature"
                          >
                            <div className="flex items-center justify-center w-6 h-6 bg-green-100 dark:bg-green-900 rounded-full mr-3 group-hover/feature:scale-110 transition-transform duration-300">
                              <FiCheckCircle className="w-3 h-3 text-green-600 dark:text-green-400" />
                            </div>
                            <span className="font-medium">{feature}</span>
                          </motion.li>
                        ))}
                      </ul>
                    )}

                    {/* CTA Link */}
                    <Link
                      to={`/services/${service._id}`}
                      className="inline-flex items-center text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 font-bold text-lg transition-all duration-300 group/link border-b-2 border-transparent hover:border-green-600 dark:hover:border-green-400 pb-1"
                    >
                      Discover More
                      <FiArrowRight className="ml-2 group-hover/link:translate-x-2 transition-transform duration-300" />
                    </Link>
                  </div>
                </div>

                {/* Hover Effect Line */}
                <div className="absolute bottom-0 left-8 right-8 h-1 bg-gradient-to-r from-green-500 to-emerald-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
              </motion.div>
            ))}
          </div>

          {/* No Services State */}
          {services.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              className="text-center py-16"
            >
              <div className="w-24 h-24 mx-auto mb-6 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                <FiTrendingUp className="w-12 h-12 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-700 dark:text-gray-300 mb-3">
                No Services Available
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
                Our agricultural technology solutions will be available soon.
                Check back later to explore our innovative services.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-2xl font-semibold hover:bg-green-700 transition-colors duration-300"
              >
                Get Notified
                <FiArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          )}

          {/* View All Button */}
          {services.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-center mt-16"
            >
              <Link
                to="/services"
                className="group relative bg-gradient-to-r from-green-500 to-emerald-500 text-white px-12 py-5 rounded-2xl font-bold text-lg hover:from-green-600 hover:to-emerald-600 transition-all duration-300 shadow-2xl hover:shadow-3xl hover:scale-105 inline-flex items-center gap-3 overflow-hidden"
              >
                {/* Animated Background */}
                <div className="absolute inset-0 bg-gradient-to-r from-green-600 to-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                <span className="relative z-10">Explore All Solutions</span>
                <FiArrowRight className="w-5 h-5 relative z-10 group-hover:translate-x-2 transition-transform duration-300" />

                {/* Shine Effect */}
                <div className="absolute inset-0 -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000">
                  <div className="w-1/2 h-full bg-white/20"></div>
                </div>
              </Link>

              {/* Supporting Text */}
              <p className="text-gray-500 dark:text-gray-400 mt-4 text-sm">
                Discover our complete range of agricultural technology services
              </p>
            </motion.div>
          )}
        </div>
      </section>

      <Testimonials />

      {/* CTA Section */}
      <section className="cta-section py-24 bg-gradient-to-r from-green-900 to-emerald-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.2'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          ></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            className="cta-content max-w-4xl mx-auto text-center"
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Ready to Revolutionize Your Farm?
            </h2>
            <p className="text-xl md:text-2xl text-green-100 mb-10 leading-relaxed">
              Join hundreds of successful farms across Africa that have
              increased their productivity and profitability with our innovative
              technology solutions
            </p>
            <div className="cta-buttons flex flex-col sm:flex-row gap-6 justify-center">
              <Link
                to="/contact"
                className="group bg-white text-green-700 px-10 py-4 rounded-2xl font-bold text-lg hover:bg-gray-100 hover:scale-105 transition-all duration-300 shadow-2xl inline-flex items-center gap-3"
              >
                Get Free Consultation
                <FiArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/projects"
                className="group border-2 border-white text-white px-10 py-4 rounded-2xl font-bold text-lg hover:bg-white hover:text-green-700 transition-all duration-300 hover:scale-105 inline-flex items-center gap-3"
              >
                View Case Studies
                <FiArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default Homepage;
